/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// The Soft UI Dashboard PRO Material main context
const MaterialUI = createContext();

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 PRO React reducer
function reducer(state, action) {
  let result = "";

  switch (action.type) {
    case "MINI_SIDENAV": {
      result = { ...state, miniSidenav: action.value };
      break;
    }
    case "TRANSPARENT_SIDENAV": {
      result = { ...state, transparentSidenav: action.value };
      break;
    }
    case "WHITE_SIDENAV": {
      result = { ...state, whiteSidenav: action.value };
      break;
    }
    case "SIDENAV_COLOR": {
      result = { ...state, sidenavColor: action.value };
      break;
    }
    case "TRANSPARENT_NAVBAR": {
      result = { ...state, transparentNavbar: action.value };
      break;
    }
    case "FIXED_NAVBAR": {
      result = { ...state, fixedNavbar: action.value };
      break;
    }
    case "OPEN_CONFIGURATOR": {
      result = { ...state, openConfigurator: action.value };
      break;
    }
    case "DIRECTION": {
      result = { ...state, direction: action.value };
      break;
    }
    case "LAYOUT": {
      result = { ...state, layout: action.value };
      break;
    }
    case "DARKMODE": {
      result = { ...state, darkMode: action.value };
      break;
    }
    case "LOCAL": {
      result = { ...state, apiServer: process.env.REACT_APP_API_LOCAL };
      break;
    }
    case "STAGING": {
      result = { ...state, apiServer: process.env.REACT_APP_API_STAGING };
      break;
    }
    case "PRODUCTION": {
      result = { ...state, apiServer: process.env.REACT_APP_API };
      break;
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }

  if (result.apiServer && !window?.location?.host?.includes("stadiumpeople")) {
    result.apiServer = result.apiServer.replace("stadiumpeople.com", "gignology.biz");
  }

  return result;
}

// Material Dashboard 2 PRO React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
    apiServer: window?.location?.host?.includes("stadiumpeople")
      ? process.env.REACT_APP_API
      : process.env.REACT_APP_API?.replace("stadiumpeople.com", "gignology.biz"),
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  return <MaterialUI.Provider value={[controller, dispatch]}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 PRO React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};
